import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";

import api from "../api/api";
import Swal from "sweetalert2";
import html2pdf from "html2pdf.js";
import * as XLSX from "xlsx";

import SchoolLogo from "./messiahlogo.png";

import {
    Box,
    Typography,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    MenuItem,
    Button,
    CircularProgress,
    Pagination,
    Chip,
    InputAdornment,
    Divider,
    Stack,
    IconButton,
    Tooltip,
    Avatar,
    Card,
    CardContent
} from "@mui/material";

import {
    Search as SearchIcon,
    Refresh as RefreshIcon,
    PictureAsPdf as PdfIcon,
    TableView as ExcelIcon,
    FilterAlt as FilterIcon,
    Payments as PaymentsIcon,
    AccountBalanceWallet as WalletIcon,
    People as PeopleIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    ArrowUpward as ArrowUpwardIcon,
    ArrowDownward as ArrowDownwardIcon,
    School as SchoolIcon,
    CalendarMonth as CalendarIcon,
    Download as DownloadIcon,
    Close as CloseIcon,
    Assessment as AssessmentIcon
} from "@mui/icons-material";

import {
    Bar
} from "react-chartjs-2";

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Tooltip as ChartTooltip,
    Legend
} from "chart.js";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    ChartTooltip,
    Legend
);

const COLORS = {
    navy: "#0f2742",
    navyDark: "#081a2c",
    orange: "#f97316",
    orangeDark: "#ea580c",
    green: "#16a34a",
    greenDark: "#15803d",
    red: "#dc2626",
    redDark: "#b91c1c",
    gold: "#d97706",
    blue: "#2563eb",
    slate: "#64748b",
    light: "#f8fafc",
    border: "#e2e8f0",
    text: "#0f172a",
    muted: "#64748b"
};

const formatMoney = (value) => {
    const amount = Number(value || 0);

    return `$${amount.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })}`;
};

const getNumericValue = (value) => {
    const number = Number(value);

    return Number.isFinite(number)
        ? number
        : 0;
};

const getStudentStatus = (student) => {
    const balance = getNumericValue(
        student?.balance
    );

    return balance > 0
        ? "arrears"
        : "clear";
};

const PaymentRecord = () => {
    const [data, setData] = useState([]);

    const [loading, setLoading] = useState(false);

    const [filterType, setFilterType] =
        useState("all");

    const [searchTerm, setSearchTerm] =
        useState("");

    const [page, setPage] =
        useState(1);

    const [rowsPerPage, setRowsPerPage] =
        useState(10);

    const [
        pdfGradeClass,
        setPdfGradeClass
    ] = useState("");

    const [
        pdfSemester,
        setPdfSemester
    ] = useState("");

    const [
        pdfStatus,
        setPdfStatus
    ] = useState("");

    const [
        pdfInstallment,
        setPdfInstallment
    ] = useState("");

    const [
        exportingPdf,
        setExportingPdf
    ] = useState(false);

    const [
        exportingExcel,
        setExportingExcel
    ] = useState(false);

    const fetchAllData = useCallback(
        async () => {
            setLoading(true);

            try {
                const response =
                    await api.get(
                        "/api/users/semester-payment-status-all/"
                    );

                const responseData =
                    response?.data;

                setData(
                    Array.isArray(responseData)
                        ? responseData
                        : []
                );

                setPage(1);
            } catch (error) {
                console.error(
                    "Payment records loading error:",
                    error
                );

                setData([]);

                Swal.fire({
                    icon: "error",
                    title: "Unable to Load Records",
                    text:
                        error?.response?.data?.detail ||
                        "Failed to fetch payment records.",
                    confirmButtonColor:
                        COLORS.orange
                });
            } finally {
                setLoading(false);
            }
        },
        []
    );

    useEffect(() => {
        fetchAllData();
    }, [fetchAllData]);

    const allStudents = useMemo(() => {
        const students = [];

        data.forEach((record) => {
            if (
                Array.isArray(
                    record?.clear_students
                )
            ) {
                students.push(
                    ...record.clear_students.map(
                        (student) => ({
                            ...student,
                            _status: "clear",
                            _gradeClass:
                                record.grade_class,
                            _semester:
                                record.semester
                        })
                    )
                );
            }

            if (
                Array.isArray(
                    record?.arrears_students
                )
            ) {
                students.push(
                    ...record.arrears_students.map(
                        (student) => ({
                            ...student,
                            _status: "arrears",
                            _gradeClass:
                                record.grade_class,
                            _semester:
                                record.semester
                        })
                    )
                );
            }
        });

        const uniqueStudents =
            new Map();

        students.forEach((student) => {
            const key =
                student?.id ||
                student?.student_id ||
                student?.student_name;

            if (!uniqueStudents.has(key)) {
                uniqueStudents.set(
                    key,
                    student
                );
            }
        });

        return Array.from(
            uniqueStudents.values()
        );
    }, [data]);

    const filteredStudents = useMemo(() => {
        const search =
            searchTerm
                .trim()
                .toLowerCase();

        return allStudents.filter(
            (student) => {
                const matchesStatus =
                    filterType === "all" ||
                    student._status ===
                        filterType;

                const name =
                    String(
                        student?.student_name ||
                        ""
                    ).toLowerCase();

                const studentId =
                    String(
                        student?.student_id ||
                        student?.id ||
                        ""
                    ).toLowerCase();

                const matchesSearch =
                    !search ||
                    name.includes(search) ||
                    studentId.includes(search);

                return (
                    matchesStatus &&
                    matchesSearch
                );
            }
        );
    }, [
        allStudents,
        filterType,
        searchTerm
    ]);

    const paginatedStudents =
        useMemo(() => {
            const start =
                (page - 1) *
                rowsPerPage;

            return filteredStudents.slice(
                start,
                start + rowsPerPage
            );
        }, [
            filteredStudents,
            page,
            rowsPerPage
        ]);

    const totalClearStudents =
        useMemo(
            () =>
                allStudents.filter(
                    (student) =>
                        student._status ===
                        "clear"
                ).length,
            [allStudents]
        );

    const totalArrearsStudents =
        useMemo(
            () =>
                allStudents.filter(
                    (student) =>
                        student._status ===
                        "arrears"
                ).length,
            [allStudents]
        );

    const totals = useMemo(() => {
        return data.reduce(
            (accumulator, record) => {
                accumulator.clear +=
                    getNumericValue(
                        record?.totals
                            ?.clear_total
                    );

                accumulator.arrears +=
                    getNumericValue(
                        record?.totals
                            ?.arrears_total
                    );

                return accumulator;
            },
            {
                clear: 0,
                arrears: 0
            }
        );
    }, [data]);

    const financialTotals =
        useMemo(() => {
            return allStudents.reduce(
                (accumulator, student) => {
                    accumulator.totalFee +=
                        getNumericValue(
                            student?.total_fee
                        );

                    accumulator.totalPaid +=
                        getNumericValue(
                            student?.total_paid
                        );

                    accumulator.balance +=
                        getNumericValue(
                            student?.balance
                        );

                    return accumulator;
                },
                {
                    totalFee: 0,
                    totalPaid: 0,
                    balance: 0
                }
            );
        }, [allStudents]);

    const chartData = useMemo(
        () => ({
            labels: [
                "Clear Students",
                "Students in Arrears"
            ],
            datasets: [
                {
                    label: "Students",
                    data: [
                        totalClearStudents,
                        totalArrearsStudents
                    ],
                    backgroundColor: [
                        COLORS.green,
                        COLORS.red
                    ],
                    borderRadius: 8,
                    borderSkipped: false,
                    barThickness: 55
                }
            ]
        }),
        [
            totalClearStudents,
            totalArrearsStudents
        ]
    );

    const chartOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: false
                },
                tooltip: {
                    backgroundColor:
                        COLORS.navy,
                    padding: 12,
                    titleFont: {
                        size: 13
                    },
                    bodyFont: {
                        size: 13
                    }
                }
            },
            scales: {
                x: {
                    grid: {
                        display: false
                    },
                    ticks: {
                        font: {
                            size: 12
                        }
                    }
                },
                y: {
                    beginAtZero: true,
                    grid: {
                        color:
                            "rgba(148,163,184,0.18)"
                    },
                    ticks: {
                        precision: 0
                    }
                }
            }
        }),
        []
    );

    const uniqueGradeClasses =
        useMemo(
            () =>
                [
                    ...new Set(
                        data
                            .map(
                                (record) =>
                                    record?.grade_class
                            )
                            .filter(Boolean)
                    )
                ].sort(),
            [data]
        );

    const uniqueSemesters =
        useMemo(
            () =>
                [
                    ...new Set(
                        data
                            .map(
                                (record) =>
                                    record?.semester
                            )
                            .filter(Boolean)
                    )
                ].sort(),
            [data]
        );

    const filterPdfStudents =
        useCallback(() => {
            if (
                !pdfGradeClass ||
                !pdfSemester ||
                !pdfStatus ||
                !pdfInstallment
            ) {
                return [];
            }

            const matchingRecords =
                data.filter(
                    (record) =>
                        record?.grade_class ===
                            pdfGradeClass &&
                        record?.semester ===
                            pdfSemester
                );

            const students = [];

            matchingRecords.forEach(
                (record) => {
                    const studentList =
                        pdfStatus === "clear"
                            ? record?.clear_students ||
                              []
                            : record?.arrears_students ||
                              [];

                    studentList.forEach(
                        (student) => {
                            const payment =
                                (
                                    student?.payments ||
                                    []
                                ).find(
                                    (item) =>
                                        item?.installment ===
                                        pdfInstallment
                                );

                            if (
                                !payment
                            ) {
                                return;
                            }

                            const matchesStatus =
                                pdfStatus ===
                                "clear"
                                    ? payment.is_installment_clear
                                    : getNumericValue(
                                          payment.installment_balance
                                      ) >
                                      0;

                            if (
                                matchesStatus
                            ) {
                                students.push({
                                    ...student,
                                    _payment:
                                        payment
                                });
                            }
                        }
                    );
                }
            );

            return students;
        }, [
            data,
            pdfGradeClass,
            pdfSemester,
            pdfStatus,
            pdfInstallment
        ]);

    const validateExportFilters =
        () => {
            if (
                !pdfGradeClass ||
                !pdfSemester ||
                !pdfStatus ||
                !pdfInstallment
            ) {
                Swal.fire({
                    icon: "warning",
                    title: "Complete the Filters",
                    text:
                        "Please select Grade Class, Semester, Status, and Installment before exporting.",
                    confirmButtonColor:
                        COLORS.orange
                });

                return false;
            }

            return true;
        };

    const downloadPDF = async () => {
        if (!validateExportFilters()) {
            return;
        }

        const students =
            filterPdfStudents();

        if (!students.length) {
            Swal.fire({
                icon: "info",
                title: "No Matching Students",
                text:
                    "No students match the selected report filters.",
                confirmButtonColor:
                    COLORS.orange
            });

            return;
        }

        const element =
            document.getElementById(
                "payment-pdf-content"
            );

        if (!element) {
            return;
        }

        try {
            setExportingPdf(true);

            element.style.display =
                "block";

            await html2pdf()
                .set({
                    margin: [
                        10,
                        10,
                        18,
                        10
                    ],
                    filename:
                        "Messiah_Payment_Report.pdf",
                    image: {
                        type: "jpeg",
                        quality: 0.98
                    },
                    html2canvas: {
                        scale: 2,
                        useCORS: true,
                        backgroundColor:
                            "#ffffff"
                    },
                    jsPDF: {
                        unit: "mm",
                        format: "a4",
                        orientation:
                            "landscape"
                    }
                })
                .from(element)
                .save();
        } catch (error) {
            console.error(
                "PDF export error:",
                error
            );

            Swal.fire({
                icon: "error",
                title: "PDF Export Failed",
                text:
                    "The payment report could not be generated.",
                confirmButtonColor:
                    COLORS.orange
            });
        } finally {
            element.style.display =
                "none";

            setExportingPdf(false);
        }
    };

    const downloadExcel = () => {
        if (!validateExportFilters()) {
            return;
        }

        const students =
            filterPdfStudents();

        if (!students.length) {
            Swal.fire({
                icon: "info",
                title: "No Matching Students",
                text:
                    "No students match the selected report filters.",
                confirmButtonColor:
                    COLORS.orange
            });

            return;
        }

        try {
            setExportingExcel(true);

            const worksheetData =
                students.map(
                    (student) => {
                        const payment =
                            student?._payment ||
                            (
                                student?.payments ||
                                []
                            ).find(
                                (item) =>
                                    item?.installment ===
                                    pdfInstallment
                            );

                        return {
                            "Student Name":
                                student?.student_name ||
                                "",
                            "Student Type":
                                student?.student_type ||
                                "",
                            "Grade Class":
                                pdfGradeClass,
                            Semester:
                                pdfSemester,
                            Status:
                                pdfStatus ===
                                "clear"
                                    ? "Clear"
                                    : "Arrears",
                            "Total Fee":
                                getNumericValue(
                                    student?.total_fee
                                ),
                            "Total Paid":
                                getNumericValue(
                                    student?.total_paid
                                ),
                            Balance:
                                getNumericValue(
                                    student?.balance
                                ),
                            Installment:
                                pdfInstallment,
                            "Installment Total":
                                getNumericValue(
                                    payment?.installment_total
                                ),
                            "Installment Paid":
                                getNumericValue(
                                    payment?.amount_paid
                                ),
                            "Installment Balance":
                                getNumericValue(
                                    payment?.installment_balance
                                ),
                            "Is Installment Clear":
                                payment?.is_installment_clear
                                    ? "Yes"
                                    : "No"
                        };
                    }
                );

            const worksheet =
                XLSX.utils.json_to_sheet(
                    worksheetData
                );

            worksheet["!cols"] = [
                {
                    wch: 28
                },
                {
                    wch: 18
                },
                {
                    wch: 18
                },
                {
                    wch: 15
                },
                {
                    wch: 14
                },
                {
                    wch: 15
                },
                {
                    wch: 15
                },
                {
                    wch: 15
                },
                {
                    wch: 20
                },
                {
                    wch: 20
                },
                {
                    wch: 20
                },
                {
                    wch: 22
                },
                {
                    wch: 24
                }
            ];

            const workbook =
                XLSX.utils.book_new();

            XLSX.utils.book_append_sheet(
                workbook,
                worksheet,
                "Payments"
            );

            XLSX.writeFile(
                workbook,
                "Messiah_Payment_Report.xlsx"
            );
        } catch (error) {
            console.error(
                "Excel export error:",
                error
            );

            Swal.fire({
                icon: "error",
                title: "Excel Export Failed",
                text:
                    "The Excel report could not be generated.",
                confirmButtonColor:
                    COLORS.orange
            });
        } finally {
            setExportingExcel(false);
        }
    };

    const handleFilterChange =
        (setter) =>
        (event) => {
            setter(
                event.target.value
            );

            setPage(1);
        };

    const clearFilters = () => {
        setFilterType("all");
        setSearchTerm("");
        setPage(1);
    };

    const clearReportFilters = () => {
        setPdfGradeClass("");
        setPdfSemester("");
        setPdfStatus("");
        setPdfInstallment("");
    };

    const hasReportFilters =
        Boolean(
            pdfGradeClass ||
                pdfSemester ||
                pdfStatus ||
                pdfInstallment
        );

    const reportStudentCount =
        useMemo(
            () =>
                pdfGradeClass &&
                pdfSemester &&
                pdfStatus &&
                pdfInstallment
                    ? filterPdfStudents()
                          .length
                    : 0,
            [
                pdfGradeClass,
                pdfSemester,
                pdfStatus,
                pdfInstallment,
                filterPdfStudents
            ]
        );

    if (loading) {
        return (
            <Box className="payment-record-page">
                <style>
                    {`
                        .payment-record-page {
                            min-height: 100vh;
                            background:
                                linear-gradient(
                                    135deg,
                                    #f8fafc 0%,
                                    #eef2f7 100%
                                );
                            padding: 32px 24px;
                        }

                        .payment-loading-screen {
                            min-height: 70vh;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            flex-direction: column;
                            gap: 18px;
                        }

                        .payment-loading-icon {
                            width: 72px;
                            height: 72px;
                            border-radius: 20px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            background: #fff7ed;
                            color: #f97316;
                        }

                        @media (max-width: 600px) {
                            .payment-record-page {
                                padding: 18px 12px;
                            }
                        }
                    `}
                </style>

                <div className="payment-loading-screen">
                    <div className="payment-loading-icon">
                        <PaymentsIcon
                            sx={{
                                fontSize: 36
                            }}
                        />
                    </div>

                    <CircularProgress
                        size={32}
                        thickness={4}
                        sx={{
                            color:
                                COLORS.orange
                        }}
                    />

                    <Typography
                        sx={{
                            color:
                                COLORS.muted,
                            fontWeight: 600
                        }}
                    >
                        Loading payment records...
                    </Typography>
                </div>
            </Box>
        );
    }

    return (
        <Box className="payment-record-page">
            <style>
                {`
                    .payment-record-page {
                        min-height: 100vh;
                        background:
                            linear-gradient(
                                135deg,
                                #f8fafc 0%,
                                #eef2f7 100%
                            );
                        padding: 28px 24px 50px;
                    }

                    .payment-record-container {
                        width: 100%;
                        max-width: 1600px;
                        margin: 0 auto;
                    }

                    .payment-hero {
                        position: relative;
                        overflow: hidden;
                        border-radius: 26px !important;
                        padding: 30px;
                        margin-bottom: 20px;
                        color: #ffffff;
                        background:
                            linear-gradient(
                                135deg,
                                #0f2742 0%,
                                #102f4e 58%,
                                #173e61 100%
                            );
                        box-shadow:
                            0 22px 55px
                            rgba(15,39,66,0.18);
                    }

                    .payment-hero::before {
                        content: "";
                        position: absolute;
                        width: 330px;
                        height: 330px;
                        right: -130px;
                        top: -180px;
                        border-radius: 50%;
                        background:
                            rgba(249,115,22,0.20);
                    }

                    .payment-hero::after {
                        content: "";
                        position: absolute;
                        width: 220px;
                        height: 220px;
                        right: 15%;
                        bottom: -170px;
                        border-radius: 50%;
                        background:
                            rgba(255,255,255,0.06);
                    }

                    .hero-content {
                        position: relative;
                        z-index: 2;
                    }

                    .hero-icon-box {
                        width: 62px;
                        height: 62px;
                        border-radius: 18px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background:
                            rgba(249,115,22,0.16);
                        border:
                            1px solid
                            rgba(249,115,22,0.35);
                        color: #fb923c;
                        margin-bottom: 18px;
                    }

                    .hero-eyebrow {
                        display: block;
                        color: #fb923c !important;
                        font-size: 11px !important;
                        font-weight: 800 !important;
                        letter-spacing: 1.8px;
                        margin-bottom: 6px;
                    }

                    .hero-title {
                        color: #ffffff !important;
                        font-weight: 800 !important;
                        margin: 0 0 8px !important;
                        font-size: clamp(
                            25px,
                            4vw,
                            38px
                        ) !important;
                    }

                    .hero-description {
                        display: block;
                        max-width: 720px;
                        color:
                            rgba(255,255,255,0.74) !important;
                        line-height: 1.7;
                        font-size: 14px;
                    }

                    .hero-summary {
                        position: relative;
                        z-index: 2;
                        min-height: 150px;
                        padding: 22px;
                        border-radius: 20px;
                        background:
                            rgba(255,255,255,0.08);
                        border:
                            1px solid
                            rgba(255,255,255,0.12);
                        backdrop-filter: blur(10px);
                    }

                    .hero-summary-label {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        color:
                            rgba(255,255,255,0.62);
                        font-size: 10px;
                        font-weight: 800;
                        letter-spacing: 1.4px;
                        margin-bottom: 10px;
                    }

                    .hero-summary-value {
                        color: #ffffff;
                        font-size: 25px;
                        font-weight: 800;
                        line-height: 1.2;
                        margin-bottom: 8px;
                    }

                    .hero-summary-subtitle {
                        color:
                            rgba(255,255,255,0.65);
                        font-size: 12px;
                    }

                    .stats-grid {
                        margin-bottom: 20px;
                    }

                    .summary-card {
                        height: 100%;
                        border-radius: 20px !important;
                        border:
                            1px solid
                            #e2e8f0 !important;
                        box-shadow:
                            0 8px 25px
                            rgba(15,23,42,0.05) !important;
                        overflow: hidden;
                        position: relative;
                    }

                    .summary-card::before {
                        content: "";
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100%;
                        height: 4px;
                        background: #f97316;
                    }

                    .summary-card.green::before {
                        background: #16a34a;
                    }

                    .summary-card.red::before {
                        background: #dc2626;
                    }

                    .summary-card.navy::before {
                        background: #0f2742;
                    }

                    .summary-card-content {
                        padding: 20px !important;
                    }

                    .summary-card-top {
                        display: flex;
                        align-items: flex-start;
                        justify-content: space-between;
                        gap: 12px;
                    }

                    .summary-label {
                        color: #64748b;
                        font-size: 11px;
                        font-weight: 800;
                        letter-spacing: .8px;
                        text-transform: uppercase;
                    }

                    .summary-value {
                        margin-top: 6px;
                        color: #0f172a;
                        font-size: 27px;
                        font-weight: 800;
                        line-height: 1.15;
                    }

                    .summary-icon {
                        width: 45px;
                        height: 45px;
                        flex-shrink: 0;
                        border-radius: 14px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: #fff7ed;
                        color: #f97316;
                    }

                    .summary-card.green
                        .summary-icon {
                        background: #f0fdf4;
                        color: #16a34a;
                    }

                    .summary-card.red
                        .summary-icon {
                        background: #fef2f2;
                        color: #dc2626;
                    }

                    .summary-card.navy
                        .summary-icon {
                        background: #eff6ff;
                        color: #0f2742;
                    }

                    .summary-caption {
                        display: flex;
                        align-items: center;
                        gap: 5px;
                        margin-top: 12px;
                        color: #64748b;
                        font-size: 12px;
                    }

                    .dashboard-grid {
                        margin-bottom: 20px;
                    }

                    .chart-card,
                    .overview-card,
                    .filter-card,
                    .student-table-card,
                    .report-card {
                        border-radius: 22px !important;
                        border:
                            1px solid
                            #e2e8f0 !important;
                        box-shadow:
                            0 8px 30px
                            rgba(15,23,42,0.05) !important;
                    }

                    .section-header {
                        display: flex;
                        align-items: flex-start;
                        justify-content: space-between;
                        gap: 15px;
                        margin-bottom: 18px;
                    }

                    .section-label {
                        display: block;
                        color: #f97316;
                        font-size: 10px;
                        font-weight: 800;
                        letter-spacing: 1.5px;
                        margin-bottom: 4px;
                    }

                    .section-title {
                        margin: 0 !important;
                        color: #0f172a !important;
                        font-weight: 800 !important;
                    }

                    .section-description {
                        display: block;
                        margin-top: 5px;
                        color: #64748b;
                        font-size: 12px;
                        line-height: 1.6;
                    }

                    .chart-container {
                        height: 300px;
                        position: relative;
                    }

                    .overview-list {
                        display: flex;
                        flex-direction: column;
                        gap: 15px;
                        margin-top: 8px;
                    }

                    .overview-item {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        gap: 15px;
                        padding: 14px 15px;
                        border-radius: 14px;
                        background: #f8fafc;
                        border: 1px solid #edf2f7;
                    }

                    .overview-item-left {
                        display: flex;
                        align-items: center;
                        gap: 11px;
                    }

                    .overview-item-icon {
                        width: 36px;
                        height: 36px;
                        border-radius: 11px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }

                    .overview-item-icon.green {
                        background: #f0fdf4;
                        color: #16a34a;
                    }

                    .overview-item-icon.red {
                        background: #fef2f2;
                        color: #dc2626;
                    }

                    .overview-item-icon.orange {
                        background: #fff7ed;
                        color: #f97316;
                    }

                    .overview-item-label {
                        color: #475569;
                        font-size: 12px;
                        font-weight: 600;
                    }

                    .overview-item-value {
                        color: #0f172a;
                        font-size: 16px;
                        font-weight: 800;
                    }

                    .filter-card {
                        margin-bottom: 20px;
                    }

                    .filter-header {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        gap: 15px;
                        margin-bottom: 18px;
                    }

                    .filter-title-wrap {
                        display: flex;
                        align-items: center;
                        gap: 12px;
                    }

                    .filter-icon {
                        width: 42px;
                        height: 42px;
                        border-radius: 13px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: #fff7ed;
                        color: #f97316;
                    }

                    .filter-title {
                        color: #0f172a;
                        font-size: 16px;
                        font-weight: 800;
                    }

                    .filter-subtitle {
                        display: block;
                        margin-top: 2px;
                        color: #64748b;
                        font-size: 11px;
                    }

                    .filter-actions {
                        display: flex;
                        gap: 8px;
                        align-items: center;
                    }

                    .student-table-card {
                        overflow: hidden;
                    }

                    .table-toolbar {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        gap: 15px;
                        margin-bottom: 18px;
                    }

                    .table-count {
                        color: #64748b;
                        font-size: 12px;
                    }

                    .table-count strong {
                        color: #0f172a;
                    }

                    .payment-table
                        .MuiTableHead-root
                        .MuiTableCell-root {
                        background: #f8fafc;
                        color: #475569;
                        font-size: 11px;
                        font-weight: 800;
                        letter-spacing: .5px;
                        text-transform: uppercase;
                        border-bottom:
                            1px solid
                            #e2e8f0;
                        white-space: nowrap;
                    }

                    .payment-table
                        .MuiTableBody-root
                        .MuiTableCell-root {
                        color: #334155;
                        font-size: 13px;
                        border-bottom:
                            1px solid
                            #edf2f7;
                        vertical-align: middle;
                    }

                    .payment-table-row:hover {
                        background: #fffaf5;
                    }

                    .student-cell {
                        display: flex;
                        align-items: center;
                        gap: 12px;
                        min-width: 220px;
                    }

                    .student-avatar {
                        width: 40px;
                        height: 40px;
                        border-radius: 12px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background:
                            linear-gradient(
                                135deg,
                                #0f2742,
                                #1e4a72
                            );
                        color: #ffffff;
                        font-weight: 800;
                        flex-shrink: 0;
                    }

                    .student-name {
                        color: #0f172a;
                        font-weight: 750;
                    }

                    .student-subtitle {
                        display: block;
                        margin-top: 2px;
                        color: #94a3b8;
                        font-size: 11px;
                    }

                    .amount-paid {
                        color: #16a34a;
                        font-weight: 800;
                    }

                    .amount-balance {
                        color: #dc2626;
                        font-weight: 800;
                    }

                    .amount-cleared {
                        color: #16a34a;
                        font-weight: 800;
                    }

                    .installment-list {
                        display: flex;
                        flex-direction: column;
                        gap: 6px;
                        min-width: 210px;
                    }

                    .installment-row {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        gap: 10px;
                        padding: 6px 8px;
                        border-radius: 8px;
                        background: #f8fafc;
                        font-size: 11px;
                    }

                    .installment-name {
                        color: #334155;
                        font-weight: 700;
                    }

                    .installment-balance {
                        color: #dc2626;
                        font-weight: 700;
                    }

                    .installment-clear {
                        color: #16a34a;
                        font-weight: 700;
                    }

                    .status-chip {
                        font-weight: 700 !important;
                        font-size: 10px !important;
                    }

                    .pagination-area {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        gap: 15px;
                        flex-wrap: wrap;
                        padding-top: 20px;
                    }

                    .pagination-info {
                        color: #64748b;
                        font-size: 12px;
                    }

                    .report-card {
                        margin-top: 20px;
                    }

                    .report-filter-box {
                        padding: 18px;
                        border-radius: 16px;
                        background: #f8fafc;
                        border: 1px solid #e2e8f0;
                    }

                    .report-filter-title {
                        color: #0f172a;
                        font-size: 13px;
                        font-weight: 800;
                        margin-bottom: 15px;
                    }

                    .report-actions {
                        display: flex;
                        align-items: center;
                        gap: 10px;
                        flex-wrap: wrap;
                        margin-top: 18px;
                    }

                    .export-pdf-button {
                        background: #dc2626 !important;
                        font-weight: 700 !important;
                        box-shadow:
                            0 7px 18px
                            rgba(220,38,38,0.20) !important;
                    }

                    .export-excel-button {
                        background: #15803d !important;
                        font-weight: 700 !important;
                        box-shadow:
                            0 7px 18px
                            rgba(21,128,61,0.20) !important;
                    }

                    .report-preview {
                        margin-top: 15px;
                        padding: 12px 15px;
                        border-radius: 12px;
                        background: #eff6ff;
                        border: 1px solid #dbeafe;
                        color: #1e40af;
                        font-size: 12px;
                        font-weight: 600;
                    }

                    .mobile-student-cards {
                        display: none;
                    }

                    .mobile-student-card {
                        border:
                            1px solid
                            #e2e8f0 !important;
                        border-radius: 17px !important;
                        margin-bottom: 12px;
                        box-shadow:
                            0 5px 18px
                            rgba(15,23,42,0.04) !important;
                    }

                    .mobile-card-header {
                        display: flex;
                        align-items: flex-start;
                        justify-content: space-between;
                        gap: 10px;
                    }

                    .mobile-card-student {
                        display: flex;
                        align-items: center;
                        gap: 10px;
                    }

                    .mobile-card-name {
                        color: #0f172a;
                        font-weight: 800;
                    }

                    .mobile-card-type {
                        display: block;
                        margin-top: 3px;
                        color: #64748b;
                        font-size: 11px;
                    }

                    .mobile-financial-grid {
                        display: grid;
                        grid-template-columns:
                            repeat(2, minmax(0, 1fr));
                        gap: 10px;
                        margin-top: 16px;
                    }

                    .mobile-financial-item {
                        padding: 11px;
                        border-radius: 11px;
                        background: #f8fafc;
                    }

                    .mobile-financial-label {
                        display: block;
                        color: #94a3b8;
                        font-size: 10px;
                        font-weight: 700;
                        margin-bottom: 4px;
                        text-transform: uppercase;
                    }

                    .mobile-financial-value {
                        color: #0f172a;
                        font-size: 13px;
                        font-weight: 800;
                    }

                    .mobile-installments {
                        margin-top: 12px;
                    }

                    .empty-state {
                        padding: 60px 20px;
                        text-align: center;
                        color: #64748b;
                    }

                    .empty-state-icon {
                        width: 70px;
                        height: 70px;
                        margin: 0 auto 15px;
                        border-radius: 20px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: #f8fafc;
                        color: #94a3b8;
                    }

                    .pdf-report-container {
                        display: none;
                        background: #ffffff;
                        color: #111827;
                        padding: 28px;
                        font-family:
                            Arial,
                            Helvetica,
                            sans-serif;
                    }

                    .pdf-report-header {
                        text-align: center;
                        margin-bottom: 24px;
                    }

                    .pdf-report-logo {
                        max-height: 65px;
                        max-width: 180px;
                        margin-bottom: 10px;
                    }

                    .pdf-report-title {
                        font-size: 22px;
                        font-weight: 800;
                        margin: 0;
                    }

                    .pdf-report-subtitle {
                        margin-top: 5px;
                        font-size: 12px;
                        color: #64748b;
                    }

                    .pdf-filter-summary {
                        display: grid;
                        grid-template-columns:
                            repeat(4, 1fr);
                        border: 1px solid #d1d5db;
                        margin-bottom: 20px;
                    }

                    .pdf-filter-item {
                        padding: 8px 10px;
                        border-right:
                            1px solid #d1d5db;
                    }

                    .pdf-filter-item:last-child {
                        border-right: none;
                    }

                    .pdf-filter-label {
                        display: block;
                        color: #6b7280;
                        font-size: 8px;
                        text-transform: uppercase;
                        font-weight: 700;
                    }

                    .pdf-filter-value {
                        font-size: 10px;
                        font-weight: 700;
                    }

                    .pdf-table {
                        width: 100%;
                        border-collapse: collapse;
                        font-size: 9px;
                    }

                    .pdf-table th {
                        background: #0f2742;
                        color: #ffffff;
                        padding: 7px 6px;
                        border: 1px solid #0f2742;
                        text-align: left;
                    }

                    .pdf-table td {
                        padding: 7px 6px;
                        border: 1px solid #d1d5db;
                    }

                    .pdf-table tbody tr:nth-child(even) {
                        background: #f8fafc;
                    }

                    .pdf-footer {
                        margin-top: 22px;
                        text-align: center;
                        color: #64748b;
                        font-size: 9px;
                    }

                    @media (max-width: 1100px) {
                        .payment-record-page {
                            padding: 22px 16px 40px;
                        }

                        .payment-hero {
                            padding: 24px;
                        }

                        .pdf-filter-summary {
                            grid-template-columns:
                                repeat(2, 1fr);
                        }

                        .pdf-filter-item:nth-child(2) {
                            border-right: none;
                        }

                        .pdf-filter-item {
                            border-bottom:
                                1px solid #d1d5db;
                        }
                    }

                    @media (max-width: 767px) {
                        .payment-record-page {
                            padding: 14px 10px 30px;
                        }

                        .payment-hero {
                            padding: 21px 18px;
                            border-radius: 20px !important;
                        }

                        .hero-title {
                            font-size: 26px !important;
                        }

                        .hero-summary {
                            min-height: auto;
                        }

                        .chart-container {
                            height: 245px;
                        }

                        .section-header,
                        .table-toolbar,
                        .filter-header {
                            align-items: flex-start;
                            flex-direction: column;
                        }

                        .filter-actions {
                            width: 100%;
                        }

                        .filter-actions button {
                            flex: 1;
                        }

                        .desktop-student-table {
                            display: none;
                        }

                        .mobile-student-cards {
                            display: block;
                        }

                        .pagination-area {
                            justify-content: center;
                        }

                        .pagination-info {
                            width: 100%;
                            text-align: center;
                        }

                        .report-actions {
                            flex-direction: column;
                        }

                        .report-actions button {
                            width: 100%;
                        }

                        .pdf-filter-summary {
                            grid-template-columns: 1fr;
                        }

                        .pdf-filter-item {
                            border-right: none !important;
                        }

                        .summary-value {
                            font-size: 24px;
                        }
                    }

                    @media (max-width: 450px) {
                        .mobile-financial-grid {
                            grid-template-columns: 1fr;
                        }

                        .payment-hero {
                            padding: 18px 15px;
                        }

                        .hero-icon-box {
                            width: 52px;
                            height: 52px;
                        }
                    }
                `}
            </style>

            <Box className="payment-record-container">

                <Paper
                    className="payment-hero"
                    elevation={0}
                >
                    <Box className="hero-content">
                        <Grid
                            container
                            spacing={3}
                            alignItems="center"
                        >
                            <Grid
                                item
                                xs={12}
                                lg={8}
                            >
                                <Box className="hero-icon-box">
                                    <PaymentsIcon
                                        sx={{
                                            fontSize: 31
                                        }}
                                    />
                                </Box>

                                <Typography className="hero-eyebrow">
                                    FINANCE & ACCOUNTING
                                </Typography>

                                <Typography className="hero-title">
                                    Semester Payment Records
                                </Typography>

                                <Typography className="hero-description">
                                    Monitor student fee payments,
                                    identify outstanding balances,
                                    review installment activity,
                                    and generate professional
                                    financial reports.
                                </Typography>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                lg={4}
                            >
                                <Box className="hero-summary">
                                    <Box className="hero-summary-label">
                                        <CalendarIcon
                                            sx={{
                                                fontSize: 15
                                            }}
                                        />
                                        PAYMENT REGISTER
                                    </Box>

                                    <Box className="hero-summary-value">
                                        {allStudents.length.toLocaleString()}
                                    </Box>

                                    <Box className="hero-summary-subtitle">
                                        Students represented
                                        in the current payment
                                        records
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>

                <Grid
                    container
                    spacing={2}
                    className="stats-grid"
                >
                    <Grid
                        item
                        xs={12}
                        sm={6}
                        lg={3}
                    >
                        <Card
                            className="summary-card"
                            elevation={0}
                        >
                            <CardContent className="summary-card-content">
                                <Box className="summary-card-top">
                                    <Box>
                                        <Typography className="summary-label">
                                            Total Students
                                        </Typography>

                                        <Typography className="summary-value">
                                            {allStudents.length.toLocaleString()}
                                        </Typography>
                                    </Box>

                                    <Box className="summary-icon">
                                        <PeopleIcon />
                                    </Box>
                                </Box>

                                <Box className="summary-caption">
                                    <ArrowUpwardIcon
                                        sx={{
                                            fontSize: 14
                                        }}
                                    />

                                    Student payment records
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        sm={6}
                        lg={3}
                    >
                        <Card
                            className="summary-card green"
                            elevation={0}
                        >
                            <CardContent className="summary-card-content">
                                <Box className="summary-card-top">
                                    <Box>
                                        <Typography className="summary-label">
                                            Clear Students
                                        </Typography>

                                        <Typography className="summary-value">
                                            {totalClearStudents.toLocaleString()}
                                        </Typography>
                                    </Box>

                                    <Box className="summary-icon">
                                        <CheckCircleIcon />
                                    </Box>
                                </Box>

                                <Box className="summary-caption">
                                    Accounts without arrears
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        sm={6}
                        lg={3}
                    >
                        <Card
                            className="summary-card red"
                            elevation={0}
                        >
                            <CardContent className="summary-card-content">
                                <Box className="summary-card-top">
                                    <Box>
                                        <Typography className="summary-label">
                                            Students in Arrears
                                        </Typography>

                                        <Typography className="summary-value">
                                            {totalArrearsStudents.toLocaleString()}
                                        </Typography>
                                    </Box>

                                    <Box className="summary-icon">
                                        <WarningIcon />
                                    </Box>
                                </Box>

                                <Box className="summary-caption">
                                    Accounts with outstanding
                                    balances
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        sm={6}
                        lg={3}
                    >
                        <Card
                            className="summary-card navy"
                            elevation={0}
                        >
                            <CardContent className="summary-card-content">
                                <Box className="summary-card-top">
                                    <Box>
                                        <Typography className="summary-label">
                                            Outstanding Balance
                                        </Typography>

                                        <Typography className="summary-value">
                                            {formatMoney(
                                                financialTotals.balance
                                            )}
                                        </Typography>
                                    </Box>

                                    <Box className="summary-icon">
                                        <WalletIcon />
                                    </Box>
                                </Box>

                                <Box className="summary-caption">
                                    Total balance across
                                    records
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                <Grid
                    container
                    spacing={2}
                    className="dashboard-grid"
                >
                    <Grid
                        item
                        xs={12}
                        lg={8}
                    >
                        <Paper
                            className="chart-card"
                            elevation={0}
                            sx={{
                                p: {
                                    xs: 2,
                                    md: 3
                                }
                            }}
                        >
                            <Box className="section-header">
                                <Box>
                                    <Typography className="section-label">
                                        PAYMENT OVERVIEW
                                    </Typography>

                                    <Typography
                                        variant="h6"
                                        className="section-title"
                                    >
                                        Student Payment Status
                                    </Typography>

                                    <Typography className="section-description">
                                        Comparison of students
                                        whose recorded balances
                                        are cleared versus those
                                        with outstanding amounts.
                                    </Typography>
                                </Box>

                                <AssessmentIcon
                                    sx={{
                                        color:
                                            COLORS.orange
                                    }}
                                />
                            </Box>

                            <Box className="chart-container">
                                <Bar
                                    data={chartData}
                                    options={chartOptions}
                                />
                            </Box>
                        </Paper>
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        lg={4}
                    >
                        <Paper
                            className="overview-card"
                            elevation={0}
                            sx={{
                                p: {
                                    xs: 2,
                                    md: 3
                                },
                                height: "100%"
                            }}
                        >
                            <Box className="section-header">
                                <Box>
                                    <Typography className="section-label">
                                        FINANCIAL SNAPSHOT
                                    </Typography>

                                    <Typography
                                        variant="h6"
                                        className="section-title"
                                    >
                                        Account Overview
                                    </Typography>
                                </Box>
                            </Box>

                            <Box className="overview-list">
                                <Box className="overview-item">
                                    <Box className="overview-item-left">
                                        <Box className="overview-item-icon orange">
                                            <PaymentsIcon
                                                sx={{
                                                    fontSize: 18
                                                }}
                                            />
                                        </Box>

                                        <Typography className="overview-item-label">
                                            Total Fees
                                        </Typography>
                                    </Box>

                                    <Typography className="overview-item-value">
                                        {formatMoney(
                                            financialTotals.totalFee
                                        )}
                                    </Typography>
                                </Box>

                                <Box className="overview-item">
                                    <Box className="overview-item-left">
                                        <Box className="overview-item-icon green">
                                            <CheckCircleIcon
                                                sx={{
                                                    fontSize: 18
                                                }}
                                            />
                                        </Box>

                                        <Typography className="overview-item-label">
                                            Total Paid
                                        </Typography>
                                    </Box>

                                    <Typography className="overview-item-value">
                                        {formatMoney(
                                            financialTotals.totalPaid
                                        )}
                                    </Typography>
                                </Box>

                                <Box className="overview-item">
                                    <Box className="overview-item-left">
                                        <Box className="overview-item-icon red">
                                            <WarningIcon
                                                sx={{
                                                    fontSize: 18
                                                }}
                                            />
                                        </Box>

                                        <Typography className="overview-item-label">
                                            Outstanding
                                        </Typography>
                                    </Box>

                                    <Typography
                                        className="overview-item-value"
                                        sx={{
                                            color:
                                                COLORS.red
                                        }}
                                    >
                                        {formatMoney(
                                            financialTotals.balance
                                        )}
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>

                <Paper
                    className="filter-card"
                    elevation={0}
                    sx={{
                        p: {
                            xs: 2,
                            md: 3
                        }
                    }}
                >
                    <Box className="filter-header">
                        <Box className="filter-title-wrap">
                            <Box className="filter-icon">
                                <FilterIcon />
                            </Box>

                            <Box>
                                <Typography className="filter-title">
                                    Student Filters
                                </Typography>

                                <Typography className="filter-subtitle">
                                    Search and filter the
                                    payment register
                                </Typography>
                            </Box>
                        </Box>

                        <Box className="filter-actions">
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={
                                    <RefreshIcon />
                                }
                                onClick={
                                    fetchAllData
                                }
                            >
                                Refresh
                            </Button>

                            <Button
                                variant="text"
                                size="small"
                                startIcon={
                                    <CloseIcon />
                                }
                                onClick={
                                    clearFilters
                                }
                                disabled={
                                    filterType ===
                                        "all" &&
                                    !searchTerm
                                }
                            >
                                Clear
                            </Button>
                        </Box>
                    </Box>

                    <Grid
                        container
                        spacing={2}
                    >
                        <Grid
                            item
                            xs={12}
                            md={4}
                        >
                            <TextField
                                fullWidth
                                select
                                size="small"
                                label="Payment Status"
                                value={
                                    filterType
                                }
                                onChange={handleFilterChange(
                                    setFilterType
                                )}
                            >
                                <MenuItem value="all">
                                    All Students
                                </MenuItem>

                                <MenuItem value="clear">
                                    Clear Students
                                </MenuItem>

                                <MenuItem value="arrears">
                                    Students in Arrears
                                </MenuItem>
                            </TextField>
                        </Grid>

                        <Grid
                            item
                            xs={12}
                            md={8}
                        >
                            <TextField
                                fullWidth
                                size="small"
                                label="Search Student"
                                value={
                                    searchTerm
                                }
                                onChange={(event) => {
                                    setSearchTerm(
                                        event.target
                                            .value
                                    );
                                    setPage(1);
                                }}
                                placeholder="Search by student name or ID..."
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon
                                                sx={{
                                                    color:
                                                        COLORS.slate
                                                }}
                                            />
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                    </Grid>
                </Paper>

                <Paper
                    className="student-table-card"
                    elevation={0}
                    sx={{
                        p: {
                            xs: 2,
                            md: 3
                        }
                    }}
                >
                    <Box className="table-toolbar">
                        <Box>
                            <Typography className="section-label">
                                STUDENT REGISTER
                            </Typography>

                            <Typography
                                variant="h6"
                                className="section-title"
                            >
                                Payment Records
                            </Typography>

                            <Typography className="table-count">
                                Showing{" "}
                                <strong>
                                    {
                                        paginatedStudents.length
                                    }
                                </strong>{" "}
                                of{" "}
                                <strong>
                                    {
                                        filteredStudents.length
                                    }
                                </strong>{" "}
                                matching students
                            </Typography>
                        </Box>

                        <Chip
                            icon={
                                <PeopleIcon />
                            }
                            label={`${filteredStudents.length} Students`}
                            sx={{
                                fontWeight: 700,
                                background:
                                    "#f1f5f9"
                            }}
                        />
                    </Box>

                    {filteredStudents.length ===
                    0 ? (
                        <Box className="empty-state">
                            <Box className="empty-state-icon">
                                <PeopleIcon
                                    sx={{
                                        fontSize: 32
                                    }}
                                />
                            </Box>

                            <Typography
                                variant="h6"
                                sx={{
                                    color:
                                        COLORS.text,
                                    fontWeight: 800,
                                    mb: 1
                                }}
                            >
                                No Payment Records
                            </Typography>

                            <Typography
                                sx={{
                                    color:
                                        COLORS.muted,
                                    fontSize: 13
                                }}
                            >
                                No students match the
                                current filter or
                                search criteria.
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Box className="desktop-student-table">
                                <TableContainer>
                                    <Table
                                        className="payment-table"
                                        size="medium"
                                    >
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>
                                                    Student
                                                </TableCell>

                                                <TableCell>
                                                    Status
                                                </TableCell>

                                                <TableCell>
                                                    Student Type
                                                </TableCell>

                                                <TableCell align="right">
                                                    Total Fee
                                                </TableCell>

                                                <TableCell align="right">
                                                    Total Paid
                                                </TableCell>

                                                <TableCell align="right">
                                                    Balance
                                                </TableCell>

                                                <TableCell>
                                                    Installments
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            {paginatedStudents.map(
                                                (
                                                    student,
                                                    index
                                                ) => {
                                                    const balance =
                                                        getNumericValue(
                                                            student?.balance
                                                        );

                                                    const studentName =
                                                        student?.student_name ||
                                                        "Unknown Student";

                                                    return (
                                                        <TableRow
                                                            key={
                                                                student?.id ||
                                                                student?.student_id ||
                                                                `${studentName}-${index}`
                                                            }
                                                            className="payment-table-row"
                                                        >
                                                            <TableCell>
                                                                <Box className="student-cell">
                                                                    <Box className="student-avatar">
                                                                        {studentName
                                                                            .charAt(
                                                                                0
                                                                            )
                                                                            .toUpperCase()}
                                                                    </Box>

                                                                    <Box>
                                                                        <Typography className="student-name">
                                                                            {
                                                                                studentName
                                                                            }
                                                                        </Typography>

                                                                        <Typography className="student-subtitle">
                                                                            ID:{" "}
                                                                            {student?.student_id ||
                                                                                student?.id ||
                                                                                "N/A"}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            </TableCell>

                                                            <TableCell>
                                                                <Chip
                                                                    size="small"
                                                                    className="status-chip"
                                                                    icon={
                                                                        balance >
                                                                        0 ? (
                                                                            <WarningIcon />
                                                                        ) : (
                                                                            <CheckCircleIcon />
                                                                        )
                                                                    }
                                                                    label={
                                                                        balance >
                                                                        0
                                                                            ? "ARREARS"
                                                                            : "CLEAR"
                                                                    }
                                                                    sx={{
                                                                        color:
                                                                            balance >
                                                                            0
                                                                                ? COLORS.redDark
                                                                                : COLORS.greenDark,
                                                                        background:
                                                                            balance >
                                                                            0
                                                                                ? "#fef2f2"
                                                                                : "#f0fdf4",
                                                                        "& .MuiChip-icon":
                                                                            {
                                                                                color:
                                                                                    "inherit"
                                                                            }
                                                                    }}
                                                                />
                                                            </TableCell>

                                                            <TableCell>
                                                                <Typography
                                                                    sx={{
                                                                        fontSize:
                                                                            12,
                                                                        color:
                                                                            COLORS.muted,
                                                                        fontWeight:
                                                                            600
                                                                    }}
                                                                >
                                                                    {student?.student_type ||
                                                                        "Regular"}{" "}
                                                                    student
                                                                </Typography>
                                                            </TableCell>

                                                            <TableCell align="right">
                                                                <Typography
                                                                    sx={{
                                                                        fontWeight:
                                                                            700,
                                                                        color:
                                                                            COLORS.text
                                                                    }}
                                                                >
                                                                    {formatMoney(
                                                                        student?.total_fee
                                                                    )}
                                                                </Typography>
                                                            </TableCell>

                                                            <TableCell align="right">
                                                                <Typography className="amount-paid">
                                                                    {formatMoney(
                                                                        student?.total_paid
                                                                    )}
                                                                </Typography>
                                                            </TableCell>

                                                            <TableCell align="right">
                                                                <Typography
                                                                    className={
                                                                        balance >
                                                                        0
                                                                            ? "amount-balance"
                                                                            : "amount-cleared"
                                                                    }
                                                                >
                                                                    {formatMoney(
                                                                        balance
                                                                    )}
                                                                </Typography>
                                                            </TableCell>

                                                            <TableCell>
                                                                <Box className="installment-list">
                                                                    {(
                                                                        student?.payments ||
                                                                        []
                                                                    ).map(
                                                                        (
                                                                            payment,
                                                                            paymentIndex
                                                                        ) => {
                                                                            const installmentBalance =
                                                                                getNumericValue(
                                                                                    payment?.installment_balance
                                                                                );

                                                                            return (
                                                                                <Box
                                                                                    key={`${payment?.installment}-${paymentIndex}`}
                                                                                    className="installment-row"
                                                                                >
                                                                                    <Typography className="installment-name">
                                                                                        {
                                                                                            payment?.installment ||
                                                                                            "Installment"
                                                                                        }
                                                                                    </Typography>

                                                                                    <Typography
                                                                                        className={
                                                                                            installmentBalance >
                                                                                            0
                                                                                                ? "installment-balance"
                                                                                                : "installment-clear"
                                                                                        }
                                                                                    >
                                                                                        {installmentBalance >
                                                                                        0
                                                                                            ? `Balance ${formatMoney(
                                                                                                  installmentBalance
                                                                                              )}`
                                                                                            : "Paid"}
                                                                                    </Typography>
                                                                                </Box>
                                                                            );
                                                                        }
                                                                    )}
                                                                </Box>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                }
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>

                            <Box className="mobile-student-cards">
                                {paginatedStudents.map(
                                    (
                                        student,
                                        index
                                    ) => {
                                        const balance =
                                            getNumericValue(
                                                student?.balance
                                            );

                                        const studentName =
                                            student?.student_name ||
                                            "Unknown Student";

                                        return (
                                            <Card
                                                key={
                                                    student?.id ||
                                                    student?.student_id ||
                                                    `${studentName}-${index}`
                                                }
                                                className="mobile-student-card"
                                                elevation={0}
                                            >
                                                <CardContent>
                                                    <Box className="mobile-card-header">
                                                        <Box className="mobile-card-student">
                                                            <Box className="student-avatar">
                                                                {studentName
                                                                    .charAt(
                                                                        0
                                                                    )
                                                                    .toUpperCase()}
                                                            </Box>

                                                            <Box>
                                                                <Typography className="mobile-card-name">
                                                                    {
                                                                        studentName
                                                                    }
                                                                </Typography>

                                                                <Typography className="mobile-card-type">
                                                                    ID:{" "}
                                                                    {student?.student_id ||
                                                                        student?.id ||
                                                                        "N/A"}
                                                                </Typography>
                                                            </Box>
                                                        </Box>

                                                        <Chip
                                                            size="small"
                                                            className="status-chip"
                                                            label={
                                                                balance >
                                                                0
                                                                    ? "ARREARS"
                                                                    : "CLEAR"
                                                            }
                                                            sx={{
                                                                color:
                                                                    balance >
                                                                    0
                                                                        ? COLORS.redDark
                                                                        : COLORS.greenDark,
                                                                background:
                                                                    balance >
                                                                    0
                                                                        ? "#fef2f2"
                                                                        : "#f0fdf4"
                                                            }}
                                                        />
                                                    </Box>

                                                    <Box className="mobile-financial-grid">
                                                        <Box className="mobile-financial-item">
                                                            <span className="mobile-financial-label">
                                                                Total Fee
                                                            </span>

                                                            <span className="mobile-financial-value">
                                                                {formatMoney(
                                                                    student?.total_fee
                                                                )}
                                                            </span>
                                                        </Box>

                                                        <Box className="mobile-financial-item">
                                                            <span className="mobile-financial-label">
                                                                Total Paid
                                                            </span>

                                                            <span
                                                                className="mobile-financial-value"
                                                                style={{
                                                                    color:
                                                                        COLORS.green
                                                                }}
                                                            >
                                                                {formatMoney(
                                                                    student?.total_paid
                                                                )}
                                                            </span>
                                                        </Box>

                                                        <Box className="mobile-financial-item">
                                                            <span className="mobile-financial-label">
                                                                Balance
                                                            </span>

                                                            <span
                                                                className="mobile-financial-value"
                                                                style={{
                                                                    color:
                                                                        balance >
                                                                        0
                                                                            ? COLORS.red
                                                                            : COLORS.green
                                                                }}
                                                            >
                                                                {formatMoney(
                                                                    balance
                                                                )}
                                                            </span>
                                                        </Box>

                                                        <Box className="mobile-financial-item">
                                                            <span className="mobile-financial-label">
                                                                Student Type
                                                            </span>

                                                            <span className="mobile-financial-value">
                                                                {student?.student_type ||
                                                                    "Regular"}
                                                            </span>
                                                        </Box>
                                                    </Box>

                                                    <Box className="mobile-installments">
                                                        <Typography
                                                            sx={{
                                                                color:
                                                                    COLORS.muted,
                                                                fontSize:
                                                                    10,
                                                                fontWeight:
                                                                    800,
                                                                textTransform:
                                                                    "uppercase",
                                                                letterSpacing:
                                                                    ".6px",
                                                                mb: 1
                                                            }}
                                                        >
                                                            Installments
                                                        </Typography>

                                                        <Stack
                                                            spacing={
                                                                0.7
                                                            }
                                                        >
                                                            {(
                                                                student?.payments ||
                                                                []
                                                            ).map(
                                                                (
                                                                    payment,
                                                                    paymentIndex
                                                                ) => {
                                                                    const installmentBalance =
                                                                        getNumericValue(
                                                                            payment?.installment_balance
                                                                        );

                                                                    return (
                                                                        <Box
                                                                            key={`${payment?.installment}-${paymentIndex}`}
                                                                            sx={{
                                                                                display:
                                                                                    "flex",
                                                                                justifyContent:
                                                                                    "space-between",
                                                                                gap: 1,
                                                                                px: 1.2,
                                                                                py: 0.8,
                                                                                borderRadius:
                                                                                    1.5,
                                                                                background:
                                                                                    "#f8fafc"
                                                                            }}
                                                                        >
                                                                            <Typography
                                                                                sx={{
                                                                                    fontSize:
                                                                                        11,
                                                                                    fontWeight:
                                                                                        700
                                                                                }}
                                                                            >
                                                                                {payment?.installment ||
                                                                                    "Installment"}
                                                                            </Typography>

                                                                            <Typography
                                                                                sx={{
                                                                                    fontSize:
                                                                                        11,
                                                                                    fontWeight:
                                                                                        700,
                                                                                    color:
                                                                                        installmentBalance >
                                                                                        0
                                                                                            ? COLORS.red
                                                                                            : COLORS.green
                                                                                }}
                                                                            >
                                                                                {installmentBalance >
                                                                                0
                                                                                    ? `Balance ${formatMoney(
                                                                                          installmentBalance
                                                                                      )}`
                                                                                    : "Paid"}
                                                                            </Typography>
                                                                        </Box>
                                                                    );
                                                                }
                                                            )}
                                                        </Stack>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        );
                                    }
                                )}
                            </Box>
                        </>
                    )}

                    {filteredStudents.length >
                        0 && (
                        <Box className="pagination-area">
                            <Typography className="pagination-info">
                                Page {page} of{" "}
                                {Math.max(
                                    1,
                                    Math.ceil(
                                        filteredStudents.length /
                                            rowsPerPage
                                    )
                                )}
                            </Typography>

                            <Pagination
                                color="primary"
                                shape="rounded"
                                page={page}
                                count={Math.max(
                                    1,
                                    Math.ceil(
                                        filteredStudents.length /
                                            rowsPerPage
                                    )
                                )}
                                onChange={(
                                    _event,
                                    value
                                ) =>
                                    setPage(
                                        value
                                    )
                                }
                            />

                            <TextField
                                select
                                size="small"
                                value={
                                    rowsPerPage
                                }
                                onChange={(
                                    event
                                ) => {
                                    setRowsPerPage(
                                        Number(
                                            event
                                                .target
                                                .value
                                        )
                                    );
                                    setPage(1);
                                }}
                                sx={{
                                    width: 105
                                }}
                            >
                                <MenuItem value={10}>
                                    10 / page
                                </MenuItem>

                                <MenuItem value={20}>
                                    20 / page
                                </MenuItem>

                                <MenuItem value={50}>
                                    50 / page
                                </MenuItem>

                                <MenuItem value={100}>
                                    100 / page
                                </MenuItem>
                            </TextField>
                        </Box>
                    )}
                </Paper>

                <Paper
                    className="report-card"
                    elevation={0}
                    sx={{
                        p: {
                            xs: 2,
                            md: 3
                        }
                    }}
                >
                    <Box className="section-header">
                        <Box>
                            <Typography className="section-label">
                                REPORTING CENTER
                            </Typography>

                            <Typography
                                variant="h6"
                                className="section-title"
                            >
                                Payment Report Export
                            </Typography>

                            <Typography className="section-description">
                                Select the reporting criteria
                                before generating a PDF or
                                Excel financial report.
                            </Typography>
                        </Box>

                        <DownloadIcon
                            sx={{
                                color:
                                    COLORS.orange
                            }}
                        />
                    </Box>

                    <Divider
                        sx={{
                            mb: 2
                        }}
                    />

                    <Box className="report-filter-box">
                        <Typography className="report-filter-title">
                            Report Criteria
                        </Typography>

                        <Grid
                            container
                            spacing={2}
                        >
                            <Grid
                                item
                                xs={12}
                                sm={6}
                                md={3}
                            >
                                <TextField
                                    fullWidth
                                    select
                                    size="small"
                                    label="Grade Class"
                                    value={
                                        pdfGradeClass
                                    }
                                    onChange={handleFilterChange(
                                        setPdfGradeClass
                                    )}
                                >
                                    <MenuItem value="">
                                        Select Grade Class
                                    </MenuItem>

                                    {uniqueGradeClasses.map(
                                        (
                                            gradeClass
                                        ) => (
                                            <MenuItem
                                                key={
                                                    gradeClass
                                                }
                                                value={
                                                    gradeClass
                                                }
                                            >
                                                {
                                                    gradeClass
                                                }
                                            </MenuItem>
                                        )
                                    )}
                                </TextField>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                sm={6}
                                md={3}
                            >
                                <TextField
                                    fullWidth
                                    select
                                    size="small"
                                    label="Semester"
                                    value={
                                        pdfSemester
                                    }
                                    onChange={handleFilterChange(
                                        setPdfSemester
                                    )}
                                >
                                    <MenuItem value="">
                                        Select Semester
                                    </MenuItem>

                                    {uniqueSemesters.map(
                                        (
                                            semester
                                        ) => (
                                            <MenuItem
                                                key={
                                                    semester
                                                }
                                                value={
                                                    semester
                                                }
                                            >
                                                {
                                                    semester
                                                }
                                            </MenuItem>
                                        )
                                    )}
                                </TextField>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                sm={6}
                                md={3}
                            >
                                <TextField
                                    fullWidth
                                    select
                                    size="small"
                                    label="Status"
                                    value={
                                        pdfStatus
                                    }
                                    onChange={handleFilterChange(
                                        setPdfStatus
                                    )}
                                >
                                    <MenuItem value="">
                                        Select Status
                                    </MenuItem>

                                    <MenuItem value="clear">
                                        Clear
                                    </MenuItem>

                                    <MenuItem value="arrears">
                                        Arrears
                                    </MenuItem>
                                </TextField>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                sm={6}
                                md={3}
                            >
                                <TextField
                                    fullWidth
                                    select
                                    size="small"
                                    label="Installment"
                                    value={
                                        pdfInstallment
                                    }
                                    onChange={handleFilterChange(
                                        setPdfInstallment
                                    )}
                                >
                                    <MenuItem value="">
                                        Select Installment
                                    </MenuItem>

                                    <MenuItem value="1st Installment">
                                        1st Installment
                                    </MenuItem>

                                    <MenuItem value="2nd Installment">
                                        2nd Installment
                                    </MenuItem>

                                    <MenuItem value="3rd Installment">
                                        3rd Installment
                                    </MenuItem>

                                    <MenuItem value="4th Installment">
                                        4th Installment
                                    </MenuItem>
                                </TextField>
                            </Grid>
                        </Grid>

                        {hasReportFilters &&
                            pdfGradeClass &&
                            pdfSemester &&
                            pdfStatus &&
                            pdfInstallment && (
                                <Box className="report-preview">
                                    {reportStudentCount.toLocaleString()}{" "}
                                    student
                                    {reportStudentCount ===
                                    1
                                        ? ""
                                        : "s"}{" "}
                                    match the selected
                                    report criteria.
                                </Box>
                            )}

                        <Box className="report-actions">
                            <Button
                                variant="contained"
                                startIcon={
                                    <PdfIcon />
                                }
                                className="export-pdf-button"
                                onClick={
                                    downloadPDF
                                }
                                disabled={
                                    exportingPdf
                                }
                            >
                                {exportingPdf
                                    ? "Generating PDF..."
                                    : "Download PDF"}
                            </Button>

                            <Button
                                variant="contained"
                                startIcon={
                                    <ExcelIcon />
                                }
                                className="export-excel-button"
                                onClick={
                                    downloadExcel
                                }
                                disabled={
                                    exportingExcel
                                }
                            >
                                {exportingExcel
                                    ? "Generating Excel..."
                                    : "Download Excel"}
                            </Button>

                            <Button
                                variant="outlined"
                                startIcon={
                                    <CloseIcon />
                                }
                                onClick={
                                    clearReportFilters
                                }
                                disabled={
                                    !hasReportFilters
                                }
                            >
                                Clear Report Filters
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                <Box
                    id="payment-pdf-content"
                    className="pdf-report-container"
                >
                    <Box className="pdf-report-header">
                        <img
                            src={SchoolLogo}
                            alt="Messiah School"
                            className="pdf-report-logo"
                        />

                        <h1 className="pdf-report-title">
                            Messiah School Payment Report
                        </h1>

                        <div className="pdf-report-subtitle">
                            Student Semester Payment
                            Statement
                        </div>
                    </Box>

                    <Box className="pdf-filter-summary">
                        <Box className="pdf-filter-item">
                            <span className="pdf-filter-label">
                                Grade Class
                            </span>

                            <span className="pdf-filter-value">
                                {
                                    pdfGradeClass
                                }
                            </span>
                        </Box>

                        <Box className="pdf-filter-item">
                            <span className="pdf-filter-label">
                                Semester
                            </span>

                            <span className="pdf-filter-value">
                                {
                                    pdfSemester
                                }
                            </span>
                        </Box>

                        <Box className="pdf-filter-item">
                            <span className="pdf-filter-label">
                                Status
                            </span>

                            <span className="pdf-filter-value">
                                {pdfStatus ===
                                "clear"
                                    ? "Clear"
                                    : "Arrears"}
                            </span>
                        </Box>

                        <Box className="pdf-filter-item">
                            <span className="pdf-filter-label">
                                Installment
                            </span>

                            <span className="pdf-filter-value">
                                {
                                    pdfInstallment
                                }
                            </span>
                        </Box>
                    </Box>

                    <table className="pdf-table">
                        <thead>
                            <tr>
                                <th>
                                    Student Name
                                </th>

                                <th>
                                    Type
                                </th>

                                <th>
                                    Total Fee
                                </th>

                                <th>
                                    Total Paid
                                </th>

                                <th>
                                    Balance
                                </th>

                                <th>
                                    Installment
                                </th>

                                <th>
                                    Installment Total
                                </th>

                                <th>
                                    Installment Paid
                                </th>

                                <th>
                                    Installment Balance
                                </th>

                                <th>
                                    Clear
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            {filterPdfStudents().map(
                                (
                                    student,
                                    index
                                ) => {
                                    const payment =
                                        student?._payment ||
                                        (
                                            student?.payments ||
                                            []
                                        ).find(
                                            (
                                                item
                                            ) =>
                                                item?.installment ===
                                                pdfInstallment
                                        );

                                    return (
                                        <tr
                                            key={`${student?.id || student?.student_name}-${index}`}
                                        >
                                            <td>
                                                {
                                                    student?.student_name
                                                }
                                            </td>

                                            <td>
                                                {
                                                    student?.student_type
                                                }
                                            </td>

                                            <td>
                                                {formatMoney(
                                                    student?.total_fee
                                                )}
                                            </td>

                                            <td>
                                                {formatMoney(
                                                    student?.total_paid
                                                )}
                                            </td>

                                            <td>
                                                {formatMoney(
                                                    student?.balance
                                                )}
                                            </td>

                                            <td>
                                                {
                                                    payment?.installment
                                                }
                                            </td>

                                            <td>
                                                {formatMoney(
                                                    payment?.installment_total
                                                )}
                                            </td>

                                            <td>
                                                {formatMoney(
                                                    payment?.amount_paid
                                                )}
                                            </td>

                                            <td>
                                                {formatMoney(
                                                    payment?.installment_balance
                                                )}
                                            </td>

                                            <td>
                                                {payment?.is_installment_clear
                                                    ? "Yes"
                                                    : "No"}
                                            </td>
                                        </tr>
                                    );
                                }
                            )}
                        </tbody>
                    </table>

                    <Box className="pdf-footer">
                        Polu School Management System
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default PaymentRecord;